import { useEffect, useMemo, useRef, useState } from "react"
import { createResearchAccountRun, normalizeAccountRunStart } from "./virtualFarmerApi"
import { usePortalLanguage } from "./virtualFarmerI18n"

const positiveId = value => Number.isSafeInteger(value) && value > 0
const text = value => typeof value === "string" && value.trim().length > 0
const stableLocationId = value => text(value) && value === value.trim() && [...value].length <= 160

// This is a UI-only restriction to the authenticated portal's saved catalog,
// not a new server-side location FK or an inferred tenant/account mapping.
export const accountRunLocationChoices = (research, locations, accountId, tenantId) => {
     if (!positiveId(accountId) || !text(tenantId) || !Array.isArray(locations) || !Array.isArray(research?.account_runs)) return null
     if (locations.some(location => !stableLocationId(location?.id) || !text(location.name)
          || location.tenantId !== tenantId || locations.filter(other => other?.id === location.id).length !== 1)) return null
     const runs = research.account_runs
     if (runs.some(run => !positiveId(run?.id) || run.account_id !== accountId || !stableLocationId(run.external_location_id)
          || !text(run.name) || !normalizeAccountRunStart(run.started_at)
          || runs.filter(other => other?.id === run.id).length !== 1
          || !((run.status === "active" && run.ended_at === null)
               || (run.status === "closed" && normalizeAccountRunStart(run.ended_at)
                    && normalizeAccountRunStart(run.ended_at) > normalizeAccountRunStart(run.started_at))))) return null
     if (runs.some(run => run.status === "active"
          && runs.filter(other => other.status === "active" && other.external_location_id === run.external_location_id).length !== 1)) return null
     return locations.map(location => ({ location, activeRun: runs.find(run => run.status === "active" && run.external_location_id === location.id) || null }))
}

export const accountRunFormStart = value => {
     if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?$/.test(value)) return null
     // The input is explicitly labelled UTC, never the browser's implicit zone.
     return normalizeAccountRunStart(`${value.length === 16 ? `${value}:00` : value}Z`)
}

export default function AccountRunCreation(props) {
     return <CreationForm key={positiveId(props.accountId) ? props.accountId : "unauthenticated"} {...props} />
}

function CreationForm({ research, locations, accountId, tenantId, enabled = false, loading = false, error = false, onRefresh }) {
     const { t } = usePortalLanguage()
     const [locationId, setLocationId] = useState("")
     const [name, setName] = useState("")
     const [start, setStart] = useState("")
     const [confirmed, setConfirmed] = useState(false)
     const [busy, setBusy] = useState(false)
     const [readRequired, setReadRequired] = useState(false)
     const [outcome, setOutcome] = useState("")
     const [failure, setFailure] = useState("")
     const scope = useRef({ mounted: true, locked: false, controller: null, catalogAtWrite: null, readCompleted: false })
     const choices = useMemo(() => accountRunLocationChoices(research, locations, accountId, tenantId), [research, locations, accountId, tenantId])
     const selected = choices?.find(choice => choice.location.id === locationId)
     const startedAt = accountRunFormStart(start)
     const validName = text(name) && [...name.trim()].length <= 160
     const available = enabled === true && choices !== null && !loading && !error && typeof onRefresh === "function"

     useEffect(() => {
          const current = scope.current
          current.mounted = true
          return () => { current.mounted = false; current.controller?.abort() }
     }, [])
     useEffect(() => {
          setConfirmed(false)
          const current = scope.current
          if (!busy && !loading && !error && current.readCompleted && choices !== null && research !== current.catalogAtWrite) {
               current.readCompleted = false
               setReadRequired(false)
               setOutcome(previous => previous === "savedRefreshFailed" ? "saved" : previous)
          }
     }, [research, choices, busy, loading, error])

     const refresh = async () => {
          let refreshed
          try { refreshed = await onRefresh() } catch (_) { refreshed = { refreshed: false } }
          if (scope.current.mounted) scope.current.readCompleted = refreshed?.refreshed === true
          return refreshed?.refreshed === true
     }
     const recover = async () => {
          const current = scope.current
          if (current.locked || typeof onRefresh !== "function") return
          current.locked = true; setBusy(true)
          try { await refresh() } finally { current.locked = false; if (current.mounted) setBusy(false) }
     }
     const submit = async event => {
          event.preventDefault()
          const current = scope.current
          if (current.locked || readRequired || !available || !selected || selected.activeRun || !validName || !startedAt || !confirmed) return
          current.locked = true
          current.catalogAtWrite = research
          current.readCompleted = false
          current.controller = new AbortController()
          setBusy(true); setReadRequired(true); setOutcome(""); setFailure(""); setConfirmed(false)
          let saved = false
          try {
               const result = await createResearchAccountRun({ name: name.trim(), external_location_id: selected.location.id, started_at: startedAt }, { signal: current.controller.signal })
               if (!current.mounted) return
               saved = result?.ok === true && positiveId(result.account_run_id) && typeof result.public_id === "string" && /^account-run-[a-f0-9]{32}$/.test(result.public_id) && result.status === "active"
               if (saved) { setOutcome("saved"); setLocationId(""); setName(""); setStart("") }
               else setFailure("uncertain")
          } catch (requestError) {
               if (!current.mounted) return
               setFailure(requestError.status === 409 ? "conflict" : [401, 403, 404, 422].includes(requestError.status) ? "notAllowed" : "uncertain")
          } finally {
               // A committed creation and its following read are independent.
               // Every attempted write is held until a fresh catalog is read;
               // a missing/late response never triggers another POST.
               if (current.mounted) {
                    const refreshed = await refresh()
                    if (current.mounted && saved && !refreshed) setOutcome("savedRefreshFailed")
               }
               current.locked = false; current.controller = null
               if (current.mounted) setBusy(false)
          }
     }
     const edit = setter => event => { setter(event.target.value); setConfirmed(false); setOutcome(""); setFailure("") }

     return <section className="vf-card" data-testid="account-run-creation">
          <div className="vf-card-heading"><div><h3>{t("runCreate.title")}</h3><p>{t("runCreate.intro")}</p></div></div>
          {outcome && <p role="status">{t(`runCreate.${outcome}`)}</p>}
          {failure && <p className="vf-error-banner" role="alert">{t(`runCreate.error.${failure}`)}</p>}
          {readRequired && !busy && <button type="button" className="vf-secondary-button" onClick={recover}>{t("runCreate.refresh")}</button>}
          {!available ? <p role="status">{t("runCreate.unavailable")}</p> : !choices.length ? <p>{t("runCreate.noLocations")}</p> : <form className="vf-research-form" onSubmit={submit}>
               <label>{t("mapping.location")}<select value={selected ? locationId : ""} disabled={busy || readRequired} required onChange={edit(setLocationId)}>
                    <option value="">{t("assignment.select")}</option>{choices.map(({ location, activeRun }) => <option key={location.id} value={location.id} disabled={Boolean(activeRun)}>{location.name}{activeRun ? ` · ${t("runCreate.activeRun", { name: activeRun.name })}` : ""}</option>)}
               </select></label>
               {choices.every(choice => choice.activeRun) && <p role="status">{t("runCreate.allActive")}</p>}
               <label>{t("runCreate.name")}<input required maxLength="160" value={name} disabled={busy || readRequired} onChange={edit(setName)} /></label>
               <label>{t("runCreate.start")}<input type="datetime-local" step="1" required value={start} disabled={busy || readRequired} onChange={edit(setStart)} /></label>
               <p>{t("runCreate.timeHelp")}</p>
               {start && <p role="status">{startedAt ? t("runCreate.utcPreview", { time: startedAt }) : t("runCreate.invalidStart")}</p>}
               <label className="vf-checkbox"><input type="checkbox" checked={confirmed} disabled={busy || readRequired || !selected || Boolean(selected.activeRun) || !validName || !startedAt} onChange={event => setConfirmed(event.target.checked)} /><span>{t("runCreate.confirm")}</span></label>
               <button type="submit" className="vf-primary-button" disabled={busy || readRequired || !selected || Boolean(selected.activeRun) || !validName || !startedAt || !confirmed}>{t(busy ? "runCreate.saving" : "runCreate.save")}</button>
          </form>}
          <p className="vf-trend-note">{t("runCreate.boundary")}</p>
     </section>
}
